"use client"

import React, { useEffect, useState, useRef } from "react"
import { Message } from "@locmod/intl"
import cx from "classnames"

import Betslip from "compositions/Betslip/Betslip"
import AcceptedBets from "compositions/AcceptedBets/AcceptedBets"

import messages from "./messages"
import { useSearchParams } from "next/navigation"
import decompressData from "src/utils/decompressData"
import decompressOvertimeData from "src/utils/decompressOvertimeData"
import decompressSxData from "src/utils/decompressSxData"
import { detectDataType } from "src/utils/detectDataType"


import { useBaseBetslip } from "@azuro-org/sdk"
import { openModal } from "@locmod/modal"
import { useChainId } from "wagmi"
import OvertimeBetslip from "../Betslip/OvertimeBetslip"
import { useTicket } from "@/contexts/TicketContext"
import SxBetslip from "../Betslip/SxBetslip"
import logger from "@/utils/logger"
import useChainProviderHook, { AppChainProvider } from "@/hooks/useChainProvider"
import { useDetailedSxBetslip } from "@/contexts/DetailedSxBetslipContext"
import { useIsMounted } from "@/hooks"

const tabs = ["betslip", "myBets"] as const


const TabbedBetslip: React.FC = () => {
  const [activeTab, setActiveTab] = useState<(typeof tabs)[number]>("betslip")
  const [isProcessingData, setIsProcessingData] = useState(false)
  const hasProcessedDataRef = useRef(false)
  const hasSetProviderOverrideRef = useRef(false)
  const searchParams = useSearchParams()
  const { addItem, clear, items } = useBaseBetslip()
  const { updateTicket, removeAll } = useTicket()
  const { addToTicket, removeAll: removeSxAll } = useDetailedSxBetslip()
  const currentChainId = useChainId()
  const isMounted = useIsMounted()
  useEffect(() => {
    const data = searchParams!.get("data")
    if (data) {
      sessionStorage.setItem("data", data)

      // Clean up URL without page reload
      window.history.replaceState({}, "", "/")
    }
  }, [searchParams])

  // Second useEffect: Process data from sessionStorage when at root URL
  useEffect(() => {
    const processStoredData = async () => {
      if (!isMounted) {return}
      const data = sessionStorage.getItem("data")
      if (data && !hasProcessedDataRef.current) {
        try {
          setIsProcessingData(true)

          // Extract target chainId and data type from the copybet data
          const { chainId: targetChainId, dataType } = detectDataType(data)
          console.log("Target chainId:", targetChainId, "Data type:", dataType, "Current chainId:", currentChainId)

          // Check if we need to switch chains or provider
          const needsChainSwitch = targetChainId && currentChainId && targetChainId !== currentChainId
          const needsProviderSwitch = targetChainId === currentChainId && targetChainId === 42161 && dataType && !hasSetProviderOverrideRef.current // Same Arbitrum chain but different provider

          if (needsChainSwitch || needsProviderSwitch) {
            console.log("Chain/provider switch needed. Target chainId:", targetChainId, "Data type:", dataType, "Current chainId:", currentChainId)

            // For Arbitrum, set the provider override based on detected data type
            if (targetChainId === 42161 && dataType && !hasSetProviderOverrideRef.current) {
              const { setChainProviderOverride } = await import('@/hooks/useChainProvider')

              if (dataType === AppChainProvider.SxBet) {
                console.log("Setting provider override to SX for Arbitrum")
                setChainProviderOverride(42161, "sx", false, false, true)
                hasSetProviderOverrideRef.current = true
              } else if (dataType === AppChainProvider.Overtime) {
                console.log("Setting provider override to Overtime for Arbitrum")
                setChainProviderOverride(42161, "overtime", true, false, false)
                hasSetProviderOverrideRef.current = true
              }

              // If we only need provider switch (same chain), process immediately
              if (!needsChainSwitch) {
                console.log("Provider override set, processing copybet data immediately")
                // Small delay to ensure provider override takes effect
                setTimeout(() => {
                  // Re-trigger the processing after provider switch
                  const storedData = sessionStorage.getItem("data")
                  if (storedData) {
                    hasProcessedDataRef.current = false
                    // The useEffect will automatically re-run due to provider change
                  }
                }, 100)
                setIsProcessingData(false)
                return
              }
            }

            // Show chain switch modal for actual chain switches
            if (needsChainSwitch) {
              openModal("SwitchNetworkModal", {
                chainId: targetChainId as any,
                onFinish: () => {
                  // Chain switch completed, resume processing
                  setTimeout(() => {
                  // Re-trigger the processing after chain switch
                    const storedData = sessionStorage.getItem("data")
                    if (storedData) {
                    // Reset the flag so processing can happen again
                      hasProcessedDataRef.current = false
                      hasSetProviderOverrideRef.current = false // Reset provider override flag too
                      // The useEffect will automatically re-run due to chainId change
                    }
                  }, 500)
                }
              })
            }

            setIsProcessingData(false)
            return
          }

          // Data type was already detected above, no need to detect again
          // Debug logging to help identify data type detection issues
          console.log("Processing data with type:", dataType, "Data length:", data.length)

          if (!dataType) {
            // Unknown data type
            console.error("Unknown data type detected. Could not process copybet data.")
            openModal("ErrorModal", {
              title: "Copybet Error",
              text: "Unknown bet format. Please ensure you're using a valid copybet link.",
            })
            sessionStorage.removeItem("data")
            setIsProcessingData(false)
            hasProcessedDataRef.current = true
            return
          }

          if (dataType === AppChainProvider.SxBet) {
            // Handle SX data
            const decompressedData = decompressSxData(data)

            // Clear existing SX betslip
            removeSxAll()

            // Add a short delay to ensure clearing is complete
            await new Promise(resolve => setTimeout(resolve, 100))

            // Process SX items
            if (Array.isArray(decompressedData)) {
              // For combo bets, add all items in quick succession
              for (const item of decompressedData) {
                addToTicket(item)
                // Small delay to ensure proper state updates
                await new Promise(resolve => setTimeout(resolve, 50))
              }
            } else {
              addToTicket(decompressedData)
            }

            sessionStorage.removeItem("data")
            setActiveTab("betslip")
            setIsProcessingData(false)
          } else if (dataType === AppChainProvider.Overtime) {
            // Handle Overtime data
            console.log("Processing Overtime data...")
            const decompressedData = decompressOvertimeData(data)
            console.log("Decompressed Overtime data:", decompressedData)

            // Clear existing betslip
            removeAll()

            // Add a short delay to ensure clearing is complete
            await new Promise(resolve => setTimeout(resolve, 100))

            // Process all items at once without delays to prevent flickering
            if (Array.isArray(decompressedData)) {
              // For combo bets, add all items in quick succession
              console.log("Processing Overtime combo bet with", decompressedData.length, "items")
              for (const item of decompressedData) {
                console.log("Adding Overtime item:", item)
                updateTicket(item)
                // Small delay to ensure proper state updates
                await new Promise(resolve => setTimeout(resolve, 50))
              }
            } else {
              console.log("Adding single Overtime item:", decompressedData)
              updateTicket(decompressedData)
            }

            sessionStorage.removeItem("data")
            setActiveTab("betslip")
            setIsProcessingData(false)
          } else if (dataType === AppChainProvider.Azuro) {
            // Handle Azuro data
            console.log("Processing Azuro data...")
            const decompressedData = decompressData(data)
            console.log("Decompressed Azuro data:", decompressedData)

            // Clear existing betslip
            clear()

            // Add a short delay to ensure clearing is complete
            await new Promise(resolve => setTimeout(resolve, 100))

            // Process all items at once without delays to prevent flickering
            if (Array.isArray(decompressedData)) {
              // For combo bets, add all items in quick succession
              for (const item of decompressedData) {
                addItem({
                  conditionId: item.conditionId,
                  outcomeId: item.outcomeId,
                  isExpressForbidden: item.isExpressForbidden,
                  marketName: item.marketName,
                  game: item.game,
                  selectionName: item.selectionName,
                  gameId: item.game.gameId,
                })
                // Small delay to ensure proper state updates
                await new Promise(resolve => setTimeout(resolve, 50))
              }
            } else {
              addItem({
                conditionId: decompressedData.conditionId,
                outcomeId: decompressedData.outcomeId,
                isExpressForbidden: decompressedData.isExpressForbidden,
                marketName: decompressedData.marketName,
                game: decompressedData.game,
                selectionName: decompressedData.selectionName,
                gameId: decompressedData.game.gameId,
              })
            }

            sessionStorage.removeItem("data")
            setActiveTab("betslip")

            // Add a small delay before checking betslip state to allow Azuro SDK to update
            await new Promise(resolve => setTimeout(resolve, 200))

            // Check if betslip items were added successfully using Azuro SDK state
            console.log("Azuro betslip items after adding:", items?.length || 0)


            console.log("Azuro copybet successful, betslip items:", items.length)

            setIsProcessingData(false)
          }
        } catch (error) {
          logger.error("Error processing stored data:", error)
          console.error("Detailed error:", error)
          openModal("ErrorModal", {
            title: "Copybet Error",
            text: "Failed to process the copied bet data. Please try again.",
          })
          sessionStorage.removeItem("data")
          setIsProcessingData(false)
          hasProcessedDataRef.current = true
        }

        // Mark as processed after any outcome
        hasProcessedDataRef.current = true
        hasSetProviderOverrideRef.current = false // Reset provider override flag for next copybet
      }
    }

    processStoredData()
  }, [addItem, clear, updateTicket, removeAll, addToTicket, removeSxAll, currentChainId, isMounted]) // Re-run when chainId changes to handle post-switch processing

  const { isOvertimeChain, isAzuroChain, isSXChain } = useChainProviderHook()

  return (
    <>
      <div className="bg-accent-grey-980 rounded-md border border-accent-grey-800 p-1 flex items-center">
        {tabs.map(tab => {
          const isActive = activeTab === tab

          return (
            <button
              key={tab}
              className={cx("w-full p-2 text-center rounded-sm", {
                "text-accent-grey-200 hover:text-accent-grey-30": !isActive,
                "text-accent-grey-30 bg-accent-grey-950": isActive,
              })}
              onClick={() => setActiveTab(tab)}
            >
              <Message value={messages[tab]} />
            </button>
          )
        })}
      </div>
      {(() => {
        switch (activeTab) {
          case "betslip": {
            if (isProcessingData) {
              return (
                <div className="flex justify-center items-center p-8">
                  <div className="text-accent-grey-300">
                    Processing bet data...
                  </div>
                </div>
              )
            }
            switch (true) {
              case isOvertimeChain: {
                return <OvertimeBetslip />
              }
              case isSXChain: {
                return <SxBetslip />
              }
              case isAzuroChain: {
                return <Betslip />
              }
              default: {
                return <Betslip />
              }
            }
          }
          case "myBets": {
            return <AcceptedBets />
          }
          default: {
            return null
          }
        }
      })()}
    </>
  )
}

export default TabbedBetslip
